'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Flag } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import {
  AmbitionContent,
  getAmbitionSubtitle,
} from '../settings-content/AmbitionContent';
import type { RenderAmbition } from '@/types/render';
import { SettingsSectionRow } from './SettingsSection';

interface AmbitionSectionProps {
  selectedAmbition: RenderAmbition;
  onAmbitionChange: (ambition: RenderAmbition) => void;
  hasChanges?: boolean;
}

/**
 * Design ambition — how far the new design may travel from the house standing
 * today. A creative input (WHAT to render), not an engine control, so it lives
 * in the first settings card. Main angle + Explore only: derivative views
 * inherit the main render's design, and Refine boldness is carried by the
 * user's own wish text. Mirrors Apple `AmbitionPickerView` / Android
 * `AmbitionPanelContent`.
 */
export function AmbitionSection({
  selectedAmbition,
  onAmbitionChange,
  hasChanges = false,
}: AmbitionSectionProps) {
  const t = useTranslations('render.settings.ambition');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  return (
    <>
      <SettingsSectionRow
        icon={Flag}
        title={t('title')}
        subtitle={getAmbitionSubtitle(selectedAmbition, t)}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <AmbitionContent
              selectedAmbition={selectedAmbition}
              onAmbitionChange={onAmbitionChange}
              variant="compact"
              closeOnSelect={() => setIsSheetOpen(false)}
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
